import React from 'react'
import Text from './utils/Text/Text'
import Button from './utils/Button/Button'

/**
 * ColorShowcase Component
 * 
 * Demonstrates the three ways to use colors in the application:
 * 1. CSS Variables (for custom styles)
 * 2. Tailwind Classes (for utility styling)
 * 3. Component Props (for semantic components)
 */
export const ColorShowcase: React.FC = () => {
  return (
    <div className="p-8 space-y-12 bg-grey-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold mb-2" style={{ color: 'var(--primary)' }}>
          QA Genie Color System
        </h1>
        <p className="text-grey-500 mb-8">
          Demonstrating three approaches to using colors in the application
        </p>

        {/* Method 1: CSS Variables */}
        <section className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h2 className="text-2xl font-bold mb-4 text-grey-900">
            Method 1: CSS Variables
          </h2>
          <p className="text-grey-500 mb-4">
            Use CSS variables for custom inline styles
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              style={{ backgroundColor: 'var(--primary)', color: 'var(--white)' }}
              className="p-4 rounded text-center"
            >
              Primary
            </div>
            <div
              style={{ backgroundColor: 'var(--accent-red)', color: 'var(--white)' }}
              className="p-4 rounded text-center"
            >
              Accent Red
            </div>
            <div
              style={{ backgroundColor: 'var(--accent-green)', color: 'var(--white)' }}
              className="p-4 rounded text-center"
            >
              Accent Green
            </div>
            <div
              style={{ backgroundColor: 'var(--accent-blue)', color: 'var(--white)' }}
              className="p-4 rounded text-center"
            >
              Accent Blue
            </div>
          </div>

          <div className="mt-4 p-4 bg-grey-50 rounded">
            <code className="text-sm text-grey-700">
              {`<div style={{ backgroundColor: 'var(--primary)' }}>Primary</div>`}
            </code>
          </div>
        </section>

        {/* Method 2: Tailwind Classes */}
        <section className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h2 className="text-2xl font-bold mb-4 text-grey-900">
            Method 2: Tailwind Utility Classes
          </h2>
          <p className="text-grey-500 mb-4">
            Use Tailwind classes for rapid development
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-primary text-white p-4 rounded text-center hover:bg-primary-hover transition-colors">
              Primary
            </div>
            <div className="bg-accent-red text-white p-4 rounded text-center hover:opacity-90 transition-opacity">
              Accent Red
            </div>
            <div className="bg-accent-green text-white p-4 rounded text-center hover:opacity-90 transition-opacity">
              Accent Green
            </div>
            <div className="bg-accent-blue text-white p-4 rounded text-center hover:opacity-90 transition-opacity">
              Accent Blue
            </div>
          </div>

          <div className="mt-4 p-4 bg-grey-50 rounded">
            <code className="text-sm text-grey-700">
              {`<div className="bg-primary text-white hover:bg-primary-hover">Primary</div>`}
            </code>
          </div>
        </section>

        {/* Method 3: Component Props */}
        <section className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h2 className="text-2xl font-bold mb-4 text-grey-900">
            Method 3: Component Props
          </h2>
          <p className="text-grey-500 mb-4">
            Use semantic component props for consistency
          </p>
          
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button variant="primary">Primary Button</Button>
              <Button variant="secondary">Secondary Button</Button>
              <Button variant="danger">Danger Button</Button>
            </div>

            <div className="flex flex-wrap gap-3">
              <Text color="primary">Primary Text</Text>
              <Text color="accent-red">Error Text</Text>
              <Text color="accent-green">Success Text</Text>
              <Text color="500">Body Text</Text>
            </div>
          </div>

          <div className="mt-4 p-4 bg-grey-50 rounded">
            <code className="text-sm text-grey-700">
              {`<Button variant="primary">Primary Button</Button>`}
              <br />
              {`<Text color="primary">Primary Text</Text>`}
            </code>
          </div>
        </section>

        {/* Color Palette Reference */}
        <section className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <h2 className="text-2xl font-bold mb-4 text-grey-900">
            Color Palette Reference
          </h2>
          
          {/* Primary Colors */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-grey-800">Primary Colors</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <ColorSwatch name="Primary" color="var(--primary)" />
              <ColorSwatch name="Primary Hover" color="var(--primary-hover)" />
              <ColorSwatch name="Primary 50" color="var(--primary-50)" />
              <ColorSwatch name="Primary 100" color="var(--primary-100)" />
              <ColorSwatch name="Primary 400" color="var(--primary-400)" />
            </div>
          </div>

          {/* Grey Scale */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-grey-800">Grey Scale</h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              <ColorSwatch name="Grey 50" color="var(--grey-50)" />
              <ColorSwatch name="Grey 100" color="var(--grey-100)" />
              <ColorSwatch name="Grey 300" color="var(--grey-300)" />
              <ColorSwatch name="Grey 500" color="var(--grey-500)" />
              <ColorSwatch name="Grey 700" color="var(--grey-700)" />
              <ColorSwatch name="Grey 900" color="var(--grey-900)" />
            </div>
          </div>

          {/* Accent Colors */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-grey-800">Accent Colors</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ColorSwatch name="Red" color="var(--accent-red)" />
              <ColorSwatch name="Green" color="var(--accent-green)" />
              <ColorSwatch name="Blue" color="var(--accent-blue)" />
              <ColorSwatch name="Yellow" color="var(--accent-yellow)" />
              <ColorSwatch name="Orange" color="var(--accent-orange)" />
              <ColorSwatch name="Violet" color="var(--accent-violet)" />
              <ColorSwatch name="Pink" color="var(--accent-pink)" />
              <ColorSwatch name="Silver" color="var(--accent-silver)" />
            </div>
          </div>
        </section>

        {/* Usage Guidelines */}
        <section className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-2xl font-bold mb-4 text-grey-900">
            Usage Guidelines
          </h2>
          
          <div className="space-y-4 text-grey-700">
            <div>
              <h3 className="font-semibold text-grey-900 mb-2">Primary Color</h3>
              <p>Use for primary CTAs, active states, and brand elements</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-grey-900 mb-2">Grey Scale</h3>
              <p>Use for text hierarchy, backgrounds, borders, and disabled states</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-grey-900 mb-2">Accent Colors</h3>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><span className="text-accent-red font-medium">Red</span>: Errors, destructive actions</li>
                <li><span className="text-accent-green font-medium">Green</span>: Success, confirmations</li>
                <li><span className="text-accent-blue font-medium">Blue</span>: Info, links</li>
                <li><span className="text-accent-yellow font-medium">Yellow</span>: Warnings</li>
                <li><span className="text-accent-orange font-medium">Orange</span>: Notifications</li>
                <li><span className="text-accent-violet font-medium">Violet</span>: Special features</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

// Helper component for color swatches
const ColorSwatch: React.FC<{ name: string; color: string }> = ({ name, color }) => {
  return (
    <div className="flex flex-col items-center">
      <div
        style={{ backgroundColor: color }}
        className="w-full h-16 rounded-lg shadow-sm border border-grey-100"
      />
      <span className="text-xs text-grey-600 mt-2 text-center">{name}</span>
    </div>
  )
}

export default ColorShowcase
